import React, { FC, useCallback, useEffect } from 'react';
import {
  MilestoneStyled,
  GripIconStyled,
  MilestoneListContainerStyled,
  MilestoneSelectedCountStyled,
  MilestoneTextStyled,
  MilestoneHoursStyled,
} from './styled';
import { Box, VisuallyHidden, Heading, theme } from '@bechtel/gds';
import { UnitOfMeasure } from '@bechtel/types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { MilestoneDroppableProps, MilestoneDraggableProps } from './types';
import { map, filter, indexOf } from 'lodash';
import { bankersRound, usePlanning, DND_IDENTIFIER } from '@bechtel/client-common';
import { useDragPermission } from '../hooks';
import produce from 'immer';
import { t, Trans } from '@lingui/macro';

export const Milestone: FC<MilestoneDraggableProps> = ({
  commodityMilestone: milestone,
  dragIndex,
  draggableId,
  setSelectedMilestoneDraggableIds,
  selectedMilestoneIndex,
  selectedMilestonesCount,
  isGhosting,
  milestoneIdentifiers,
}) => {
  const { unitOfMeasure } = usePlanning();
  const canPlanMilestone = useDragPermission([milestone]);

  useEffect(() => {
    if (milestoneIdentifiers)
      milestoneIdentifiers.current.set(milestone.id, {
        dragIdentifier: draggableId,
        canPlan: canPlanMilestone,
      });
  }, [canPlanMilestone, draggableId, milestone, milestoneIdentifiers]);

  const handleClick = useCallback(
    (event) => {
      if (event.defaultPrevented) return;
      // NOTE: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
      if (event.button !== 0) return;
      if (!canPlanMilestone) return;

      event.preventDefault();

      setSelectedMilestoneDraggableIds(
        //@ts-ignore produce doesn't return array
        produce((draft) => {
          if (selectedMilestoneIndex > -1) {
            draft.splice(selectedMilestoneIndex, 1);
          } else {
            draft.push(draggableId);
          }
        })
      );
      // NOTE: setSelectedMilestoneDraggableIds is a pure function, linting is throwing false positive
    },
    [canPlanMilestone, draggableId, selectedMilestoneIndex]
  );
  const isSelected = selectedMilestoneIndex > -1;

  return (
    <Draggable key={draggableId} draggableId={draggableId} index={dragIndex} isDragDisabled={!canPlanMilestone}>
      {(provided, snapshot) => {
        const showSelectedCount = snapshot.isDragging && selectedMilestonesCount > 1;
        return (
          <MilestoneStyled
            canPlan={canPlanMilestone}
            data-testid={`milestone-box`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            {...snapshot}
            // isDragging={snapshot.isDragging}
            isSelected={isSelected || snapshot.isDragging}
            isGhosting={isGhosting}
            isExpired={milestone.isExpired}
            onClick={handleClick}
          >
            <Box
              data-testid={`milestone-name`}
              flexBasis={{ sm: '33%' }}
              alignSelf={{ sm: 'flex-start' }}
              style={{ minWidth: 0 }}
            >
              <MilestoneTextStyled
                isExpired={milestone.isExpired}
                isSelected={isSelected || snapshot.isDragging}
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {milestone.name}&nbsp;
              </MilestoneTextStyled>
            </Box>
            <Box data-testid={`milestone-hours`} flexBasis={{ sm: '33%' }} alignSelf={{ sm: 'flex-start' }}>
              <MilestoneHoursStyled
                isExpired={milestone.isExpired}
                isSelected={isSelected || snapshot.isDragging}
                style={{ whiteSpace: 'nowrap' }}
              >
                {unitOfMeasure === UnitOfMeasure.LINEAR_FEET
                  ? `${bankersRound(milestone.quantity.quantity, 1)} ${t`LF`}`
                  : `${bankersRound(milestone.hours, 0)} ${t`hrs`}`}
              </MilestoneHoursStyled>
            </Box>
            <GripIconStyled
              style={{ paddingLeft: `${theme.spacings[200]}` }}
              icon={canPlanMilestone ? 'grip-vertical' : 'lock'}
              isSelected={isSelected || snapshot.isDragging}
              isExpired={milestone.isExpired}
              data-testid={canPlanMilestone ? 'drag-handler' : 'no-drag-handler'}
            />
            {showSelectedCount && (
              <MilestoneSelectedCountStyled>{selectedMilestonesCount}</MilestoneSelectedCountStyled>
            )}
          </MilestoneStyled>
        );
      }}
    </Draggable>
  );
};

export const MilestoneDroppable: FC<MilestoneDroppableProps> = ({
  commodityId,
  draggableId,
  droppableId,
  commodityMilestones,
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  milestoneIdentifiers,
}) => {
  const nonExpiredMilestones = filter(commodityMilestones, { isExpired: false });
  const expiredMilestones = filter(commodityMilestones, { isExpired: true });
  return (
    <Droppable droppableId={`${droppableId}___${commodityId}`} isDropDisabled>
      {(provided, snapshot) => {
        return (
          <MilestoneListContainerStyled ref={provided.innerRef} {...provided.droppableProps} {...snapshot}>
            <VisuallyHidden>{provided.placeholder}</VisuallyHidden>
            {nonExpiredMilestones && nonExpiredMilestones.length > 0 && (
              <Box flexDirection={{ sm: 'column' }} mt={{ sm: '600' }} flexBasis={{ sm: '100%' }}>
                {map(nonExpiredMilestones, (milestone, index) => {
                  const identifier = `${draggableId}___${DND_IDENTIFIER.Milestone}___${milestone.id}`;
                  const selectedMilestoneIndex = indexOf(selectedMilestoneDraggableIds, identifier);
                  const isGhosting =
                    selectedMilestoneIndex > -1 && Boolean(draggingMilestoneId) && draggingMilestoneId !== identifier;
                  return (
                    <Box key={identifier} flexDirection={{ sm: 'column' }}>
                      <Milestone
                        commodityMilestone={milestone}
                        dragIndex={index}
                        draggableId={identifier}
                        setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                        selectedMilestoneIndex={selectedMilestoneIndex}
                        selectedMilestonesCount={selectedMilestoneDraggableIds.length}
                        isGhosting={isGhosting}
                        milestoneIdentifiers={milestoneIdentifiers}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            {expiredMilestones && expiredMilestones.length > 0 && (
              <Box flexDirection={{ sm: 'column' }} mt={{ sm: '600' }} flexBasis={{ sm: '100%' }}>
                <Box mb={{ sm: '200' }}>
                  <Heading size="300" weight="600" color="red.000">
                    <Trans>Expired Milestones</Trans>
                  </Heading>
                </Box>
                {map(expiredMilestones, (milestone, index) => {
                  const identifier = `${draggableId}___${DND_IDENTIFIER.Milestone}___${milestone.id}___${DND_IDENTIFIER.Expired}`;
                  const selectedMilestoneIndex = indexOf(selectedMilestoneDraggableIds, identifier);
                  const isGhosting =
                    selectedMilestoneIndex > -1 && Boolean(draggingMilestoneId) && draggingMilestoneId !== identifier;
                  return (
                    <Box key={milestone.id} flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
                      <Milestone
                        commodityMilestone={milestone}
                        dragIndex={index}
                        draggableId={identifier}
                        setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                        selectedMilestoneIndex={selectedMilestoneIndex}
                        selectedMilestonesCount={selectedMilestoneDraggableIds.length}
                        isGhosting={isGhosting}
                        milestoneIdentifiers={milestoneIdentifiers}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </MilestoneListContainerStyled>
        );
      }}
    </Droppable>
  );
};
